<script setup lang="ts">
/**
 * Guided company verification (/verify).
 *
 * A focused, step-by-step way to send the documents a business account
 * needs, for whichever situation the customer is in (GET /kyc `journey`):
 *   first_time  a new business, before its first order
 *   requested   an existing customer Xobo asked to verify
 *   replace     documents Xobo could not accept
 *   expired     a document that has expired
 * It opens on a welcome that explains the situation, walks one document per
 * step with guidance on what is accepted, and ends on what happens next.
 * In review, verified and not-applicable accounts land straight on the end
 * screen. Members (non-admins) cannot upload; they get an explanation.
 *
 * The plan — which documents get a step — is fixed when the flow opens, so a
 * step does not vanish the moment its document is uploaded. ?step= holds the
 * current step so refresh and Back work; ?doc= opens one document directly
 * (the Upload / Replace buttons on the Verification page link here).
 */
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  useCompanyKyc,
  canUploadType,
  type KycDocumentType,
  type KycView,
} from "../../composables/useCompanyKyc";
import { isCompanyAdminFromToken } from "../../utils/authRole";
import VerifyWelcome from "./VerifyWelcome.vue";
import VerifyDocStep from "./VerifyDocStep.vue";
import VerifyFinish from "./VerifyFinish.vue";

const route = useRoute();
const router = useRouter();
const kyc = useCompanyKyc();
const view = kyc.view;
const loading = ref(true);
const isAdmin = isCompanyAdminFromToken(localStorage.getItem("_panda"));

// ─── The plan ────────────────────────────────────────────────────────
const plan = ref<string[]>([]);

function buildPlan(v: KycView): string[] {
  const types = v.document_types;
  const needsAction = (t: KycDocumentType) =>
    (t.is_required && !t.document) ||
    t.document?.review_status === "rejected" ||
    (!!t.document?.expired && canUploadType(v, t));
  let picked: KycDocumentType[] = [];
  if (v.journey === "first_time" || v.journey === "requested") {
    // Everything, required first: a step for a document already sent shows
    // it as done and lets them replace it.
    picked = [...types.filter((t) => t.is_required), ...types.filter((t) => !t.is_required)];
  } else if (v.journey === "replace" || v.journey === "expired") {
    picked = types.filter(needsAction);
  }
  // A required document still missing, in any journey the server would take
  // it in: e.g. a type made required after the company sent its documents,
  // which holds up the review until it is uploaded.
  for (const t of types) {
    if (t.is_required && !t.document && !picked.includes(t) && canUploadType(v, t)) picked.push(t);
  }
  // A document asked for by the link (?doc=) or by the step being reloaded
  // (?step=), even outside the journey's own list, when it can be uploaded.
  for (const q of [route.query.doc, route.query.step]) {
    const asked = typeof q === "string" ? q : "";
    if (!asked || picked.some((t) => t.doc_type === asked)) continue;
    const t = types.find((x) => x.doc_type === asked);
    if (t && canUploadType(v, t)) picked.push(t);
  }
  return picked.map((t) => t.doc_type);
}

const steps = computed(() => ["welcome", ...plan.value, "finish"]);
const typeOf = (code: string) => view.value?.document_types.find((t) => t.doc_type === code) || null;

// ─── Current step ────────────────────────────────────────────────────
const stepKey = computed(() => {
  const s = typeof route.query.step === "string" ? route.query.step : "";
  return steps.value.includes(s) ? s : "welcome";
});
const stepIndex = computed(() => steps.value.indexOf(stepKey.value));
const currentType = computed(() => (["welcome", "finish"].includes(stepKey.value) ? null : typeOf(stepKey.value)));

// Each step the customer moves to is a history entry, so the browser's Back
// goes back a step. Setting up the first step replaces the entry instead.
function go(key: string, replace = false) {
  const { doc, ...rest } = route.query;
  void doc;
  const to = { query: { ...rest, step: key === "welcome" ? undefined : key } };
  return replace ? router.replace(to) : router.push(to);
}
const next = () => go(steps.value[Math.min(stepIndex.value + 1, steps.value.length - 1)]);
const back = () => go(steps.value[Math.max(stepIndex.value - 1, 0)]);
function afterUpload(submitted: boolean) {
  if (submitted) go("finish");
  else next();
}
function exit() {
  const n = route.query.next;
  // Same-site paths only, as Login's ?next.
  const safe = typeof n === "string" && n.startsWith("/") && !n.startsWith("//") && !n.includes("\\");
  router.push(safe ? n : "/request");
}

// Move focus to the new step's heading so keyboard and screen-reader users
// land on it, not at the bottom of the previous one.
const main = ref<HTMLElement | null>(null);
watch(stepKey, async () => {
  await nextTick();
  main.value?.querySelector<HTMLElement>("h1, h2")?.focus({ preventScroll: true });
  // The page scrolls inside App.vue's focus container, not the window.
  document.querySelector(".focus-container")?.scrollTo({ top: 0, behavior: "smooth" });
});

onMounted(async () => {
  await kyc.load(true);
  loading.value = false;
  const v = view.value;
  if (!v) return;
  plan.value = buildPlan(v);
  const asked = typeof route.query.doc === "string" ? route.query.doc : "";
  // Nothing to upload (in review, verified, not applicable — or a rejection
  // over a type since archived): straight to the end, which says what's next.
  if (asked && plan.value.includes(asked)) go(asked, true);
  else if (!plan.value.length) go("finish", true);
});

// ─── Rail ────────────────────────────────────────────────────────────
type RailState = "done" | "attention" | "todo";
function railState(code: string): RailState {
  const d = typeOf(code)?.document;
  if (!d) return "todo";
  if (d.review_status === "rejected" || d.expired) return "attention";
  return "done";
}
const requiredInPlan = computed(() => plan.value.filter((c) => typeOf(c)?.is_required));
const doneRequired = computed(() => requiredInPlan.value.filter((c) => railState(c) === "done").length);
const percent = computed(() =>
  requiredInPlan.value.length ? Math.round((doneRequired.value / requiredInPlan.value.length) * 100) : 100,
);
const companyName = computed(() => view.value?.company.company_name || "your company");
</script>

<template>
  <div class="vf">
    <header class="vf-top">
      <router-link to="/request" class="vf-top__logo" aria-label="Xobo home">
        <img src="/xobo.png" alt="XOBO" />
      </router-link>
      <span class="vf-top__title">Verify {{ companyName }}</span>
      <button type="button" class="vf-top__exit" @click="exit">
        {{ stepKey === "finish" ? "Close" : "Do this later" }}
      </button>
    </header>

    <div v-if="loading" class="vf-loading" v-loading="true"></div>

    <div v-else-if="!view" class="vf-body vf-body--single">
      <main class="vf-card vf-message">
        <h1 tabindex="-1">We couldn't load your verification</h1>
        <p>{{ kyc.error.value || "Please try again in a moment." }}</p>
        <button type="button" class="vf-btn" @click="$router.go(0)">Try again</button>
      </main>
    </div>

    <!-- Members can see what is needed but not upload. -->
    <div v-else-if="!isAdmin" class="vf-body vf-body--single">
      <main ref="main" class="vf-card vf-message">
        <span class="vf-badge-icon" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
        </span>
        <h1 tabindex="-1">An admin of {{ companyName }} sends these</h1>
        <p>
          Company documents are uploaded by the admins of your account. Ask one of them to sign in and open
          <strong>Account → Verification</strong>; you can see where it stands there too.
        </p>
        <div class="vf-message__actions">
          <router-link to="/account/verification" class="vf-btn">See verification status</router-link>
          <button type="button" class="vf-btn vf-btn--ghost" @click="exit">Back to Xobo</button>
        </div>
      </main>
    </div>

    <div v-else class="vf-body" :class="{ 'vf-body--single': !plan.length }">
      <!-- Step rail -->
      <aside v-if="plan.length" class="vf-rail" aria-label="Steps">
        <div class="vf-progress">
          <div class="vf-progress__row">
            <span class="vf-progress__label">Required documents</span>
            <span class="vf-progress__value">{{ doneRequired }} of {{ requiredInPlan.length }}</span>
          </div>
          <div class="vf-progress__bar" role="progressbar" :aria-valuenow="percent" aria-valuemin="0" aria-valuemax="100">
            <span :style="{ width: percent + '%' }"></span>
          </div>
        </div>
        <ol class="vf-steps">
          <li v-for="(key, i) in steps" :key="key">
            <button
              type="button"
              class="vf-step"
              :class="[
                { 'is-current': key === stepKey },
                key !== 'welcome' && key !== 'finish' ? `is-${railState(key)}` : '',
              ]"
              :aria-current="key === stepKey ? 'step' : undefined"
              @click="go(key)"
            >
              <span class="vf-step__dot" aria-hidden="true">
                <svg v-if="key !== 'welcome' && key !== 'finish' && railState(key) === 'done'" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                <span v-else-if="key !== 'welcome' && key !== 'finish' && railState(key) === 'attention'">!</span>
                <span v-else>{{ i + 1 }}</span>
              </span>
              <span class="vf-step__text">
                <span class="vf-step__name">
                  {{ key === "welcome" ? "Before you start" : key === "finish" ? "Review and finish" : typeOf(key)?.label }}
                </span>
                <span v-if="key !== 'welcome' && key !== 'finish' && !typeOf(key)?.is_required" class="vf-step__opt">Optional</span>
              </span>
            </button>
          </li>
        </ol>
        <p class="vf-rail__privacy">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          Stored privately. Only your admins and our verification team can open them.
        </p>
      </aside>

      <!-- Phone: compact progress instead of the rail -->
      <div v-if="plan.length" class="vf-mobile-progress">
        <span>Step {{ stepIndex + 1 }} of {{ steps.length }}</span>
        <div class="vf-progress__bar"><span :style="{ width: percent + '%' }"></span></div>
      </div>

      <main ref="main" class="vf-main">
        <VerifyWelcome
          v-if="stepKey === 'welcome'"
          :view="view"
          :plan="plan.map((c) => typeOf(c)).filter(Boolean) as KycDocumentType[]"
          @start="next"
          @finish="go('finish')"
          @later="exit"
        />
        <VerifyDocStep
          v-else-if="currentType"
          :key="currentType.doc_type"
          :view="view"
          :type="currentType"
          :index="stepIndex"
          :total="steps.length"
          @uploaded="afterUpload"
          @next="next"
          @back="back"
        />
        <VerifyFinish
          v-else
          :view="view"
          :plan="plan"
          @go="go"
          @exit="exit"
        />
      </main>
    </div>
  </div>
</template>

<style>
/* Tokens for the whole flow (child components read them). */
.vf {
  --vf-navy: #12284a;
  --vf-navy-2: #1e3f73;
  --vf-ink: #0f1c2e;
  --vf-text: #475569;
  --vf-muted: #8a97ab;
  --vf-line: #e3e8f0;
  --vf-bg: #f4f6fa;
  --vf-card: #ffffff;
  --vf-red: #eb1f28;
  --vf-ok: #15803d;
  --vf-ok-bg: #effaf3;
  --vf-warn: #b45309;
  --vf-warn-bg: #fff8eb;
  --vf-bad: #b91c1c;
  --vf-bad-bg: #fef2f2;
  --vf-info-bg: #eef3fb;
  font-family: "DM Sans", sans-serif;
  color: var(--vf-ink);
  background: var(--vf-bg);
  min-height: 100%;
}
.vf *, .vf *::before, .vf *::after { box-sizing: border-box; }

.vf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  padding: 0 22px;
  border: 1.5px solid var(--vf-navy);
  border-radius: 11px;
  background: var(--vf-navy);
  color: #fff;
  font-family: inherit;
  font-size: 0.92rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.vf-btn:hover:not(:disabled) { background: var(--vf-navy-2); border-color: var(--vf-navy-2); }
.vf-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(18, 40, 74, 0.25); }
.vf-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.vf-btn--ghost { background: #fff; color: var(--vf-navy); border-color: var(--vf-line); }
.vf-btn--ghost:hover:not(:disabled) { background: #f7f9fc; border-color: #cbd5e1; color: var(--vf-navy); }
.vf-btn--link { background: none; border-color: transparent; color: var(--vf-text); padding: 0 8px; }
.vf-btn--link:hover:not(:disabled) { background: none; border-color: transparent; color: var(--vf-navy); text-decoration: underline; }

.vf-card {
  background: var(--vf-card);
  border: 1px solid var(--vf-line);
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(15, 28, 46, 0.04), 0 12px 32px -18px rgba(18, 40, 74, 0.25);
}
.vf-eyebrow {
  margin: 0 0 8px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--vf-navy-2);
}
.vf h1, .vf h2 { outline: none; text-wrap: balance; }
</style>

<style scoped>
.vf-top {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 64px;
  padding: 0 28px;
  background: #fff;
  border-bottom: 3px solid var(--vf-red);
}
.vf-top__logo img { height: 28px; display: block; }
.vf-top__title {
  flex: 1;
  min-width: 0;
  padding-left: 16px;
  border-left: 1px solid var(--vf-line);
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--vf-navy);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.vf-top__exit {
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--vf-text);
  cursor: pointer;
  padding: 8px 4px;
}
.vf-top__exit:hover { color: var(--vf-navy); text-decoration: underline; }

.vf-loading { height: 60vh; }

.vf-body {
  max-width: 1120px;
  margin: 0 auto;
  padding: 36px 28px 64px;
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 32px;
  align-items: start;
}
.vf-body--single { grid-template-columns: minmax(0, 640px); justify-content: center; }

.vf-rail {
  position: sticky;
  top: 100px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.vf-progress__row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
.vf-progress__label { font-size: 0.78rem; font-weight: 600; color: var(--vf-text); }
.vf-progress__value { font-size: 0.82rem; font-weight: 700; color: var(--vf-navy); font-variant-numeric: tabular-nums; }
.vf-progress__bar { height: 6px; border-radius: 999px; background: #dde3ec; overflow: hidden; }
.vf-progress__bar span { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #1e3f73, #12284a); transition: width 0.4s ease; }

.vf-steps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
.vf-step {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 0;
  border-radius: 12px;
  background: none;
  font-family: inherit;
  text-align: left;
  color: var(--vf-text);
  cursor: pointer;
}
.vf-step:hover { background: rgba(18, 40, 74, 0.05); }
.vf-step:focus-visible { outline: 2px solid var(--vf-navy); outline-offset: 1px; }
.vf-step.is-current { background: #fff; box-shadow: 0 1px 2px rgba(15, 28, 46, 0.06), 0 0 0 1px var(--vf-line); color: var(--vf-ink); }
.vf-step__dot {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.74rem;
  font-weight: 700;
  border: 1.5px solid #c9d2df;
  background: #fff;
  color: var(--vf-muted);
}
.vf-step.is-current .vf-step__dot { border-color: var(--vf-navy); background: var(--vf-navy); color: #fff; }
.vf-step.is-done .vf-step__dot { border-color: var(--vf-ok); background: var(--vf-ok); color: #fff; }
.vf-step.is-attention .vf-step__dot { border-color: var(--vf-bad); background: var(--vf-bad-bg); color: var(--vf-bad); }
.vf-step__text { display: flex; flex-direction: column; min-width: 0; }
.vf-step__name { font-size: 0.86rem; font-weight: 600; line-height: 1.3; }
.vf-step__opt { font-size: 0.7rem; color: var(--vf-muted); font-weight: 500; }
.vf-rail__privacy {
  margin: 0;
  display: flex;
  gap: 8px;
  font-size: 0.76rem;
  line-height: 1.5;
  color: var(--vf-muted);
}
.vf-rail__privacy svg { flex-shrink: 0; margin-top: 2px; }

.vf-mobile-progress { display: none; }

.vf-message {
  padding: 40px 36px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.vf-message h1 { margin: 6px 0 0; font-size: 1.4rem; color: var(--vf-navy); }
.vf-message p { margin: 0; max-width: 46ch; color: var(--vf-text); line-height: 1.6; }
.vf-message__actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-top: 12px; }
.vf-badge-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--vf-info-bg);
  color: var(--vf-navy);
}

@media (max-width: 900px) {
  .vf-body { grid-template-columns: minmax(0, 1fr); gap: 16px; padding: 20px 16px 48px; }
  .vf-rail { display: none; }
  .vf-mobile-progress {
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--vf-text);
  }
  .vf-top { padding: 0 16px; gap: 12px; height: 56px; }
  .vf-top__title { padding-left: 12px; font-size: 0.84rem; }
}
</style>
